import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { lazyLoadWithDimensions } from '../../../../util/uiHelpers.js';

import { AspectRatioWrapper } from '../../../../components/index.js';

import css from './YoutubeEmbed.module.css';

const RADIX = 10;
const BLACK_BG = '#000000';
const TOUCH_MOVE_THRESHOLD = 8;
const YOUTUBE_IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';
const YOUTUBE_VIDEO_ENDED_EVENT = 'youtubeVideoEnded';
const AUTOPLAY_VISIBILITY_THRESHOLD = 0.65;
const VIDEO_END_THRESHOLD_SECONDS = 1;

let youtubeIframeApiPromise = null;

const loadYouTubeIframeAPI = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (youtubeIframeApiPromise) {
    return youtubeIframeApiPromise;
  }

  youtubeIframeApiPromise = new Promise(resolve => {
    const previousReadyHandler = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReadyHandler === 'function') {
        previousReadyHandler();
      }
      resolve(window.YT);
    };

    const hasScript = document.querySelector(`script[src="${YOUTUBE_IFRAME_API_SRC}"]`);
    if (!hasScript) {
      const script = document.createElement('script');
      script.src = YOUTUBE_IFRAME_API_SRC;
      script.async = true;
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    }
  });

  return youtubeIframeApiPromise;
};

const IFrame = props => {
  const { dimensions, ...rest } = props;
  return <iframe {...dimensions} {...rest} />;
};
const LazyIFrame = lazyLoadWithDimensions(IFrame);

/**
 * Embeds a YouTube video inside an iframe that has given aspect ratio.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.youtubeVideoId video id of a youtube video
 * @param {string?} props.aspectRatio e.g. '16/9'
 * @param {boolean?} props.autoplay autoplay video muted
 * @returns {JSX.Element} an element with given aspect ratio that contains iframe showing youtube video
 */
export const YoutubeEmbed = props => {
  const {
    className,
    rootClassName,
    youtubeVideoId,
    aspectRatio = '16/9',
    autoplay = false,
  } = props;
  const [isTouchInteracting, setIsTouchInteracting] = useState(autoplay);
  const [loadedIframeId, setLoadedIframeId] = useState(null);
  const playerRef = useRef(null);
  const hasStartedPlaybackRef = useRef(false);
  const touchStartRef = useRef({ x: 0, y: 0, isSwipe: false });
  const hasSlash = aspectRatio.indexOf('/') > 0;
  const [aspectWidth, aspectHeight] = hasSlash ? aspectRatio.split('/') : [16, 9];
  const width = Number.parseInt(aspectWidth, RADIX);
  const height = Number.parseInt(aspectHeight, RADIX);
  const classes = classNames(rootClassName || css.video, className);
  const iframeId = `youtube-player-${youtubeVideoId}`;
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : undefined;
  const queryParams = new URLSearchParams({
    rel: '0',
    controls: '1',
    playsinline: '1',
    enablejsapi: '1',
    ...(origin ? { origin } : {}),
    ...(autoplay ? { autoplay: '1', mute: '1' } : {}),
  }).toString();

  const dispatchVideoEndedEvent = useCallback(() => {
    const iframe = document.getElementById(iframeId);
    iframe?.dispatchEvent(
      new CustomEvent(YOUTUBE_VIDEO_ENDED_EVENT, {
        bubbles: true,
        detail: { youtubeVideoId },
      })
    );
  }, [iframeId, youtubeVideoId]);

  const hasVideoActuallyEnded = player => {
    const duration = player?.getDuration?.() || 0;
    const currentTime = player?.getCurrentTime?.() || 0;
    return duration > 0 && currentTime >= duration - VIDEO_END_THRESHOLD_SECONDS;
  };

  useEffect(() => {
    if (loadedIframeId !== iframeId) {
      return undefined;
    }

    let isUnmounted = false;
    let visibilityObserver = null;

    loadYouTubeIframeAPI().then(YT => {
      if (isUnmounted || !YT?.Player || playerRef.current) {
        return;
      }

      playerRef.current = new YT.Player(iframeId, {
        events: {
          onReady: event => {
            if (!autoplay) {
              return;
            }

            event.target.mute();

            const iframe = document.getElementById(iframeId);
            if (!iframe || typeof IntersectionObserver === 'undefined') {
              event.target.playVideo();
              return;
            }

            visibilityObserver = new IntersectionObserver(
              entries => {
                const [entry] = entries;
                if (entry.intersectionRatio >= AUTOPLAY_VISIBILITY_THRESHOLD) {
                  event.target.playVideo();
                } else {
                  event.target.pauseVideo();
                }
              },
              { threshold: AUTOPLAY_VISIBILITY_THRESHOLD }
            );
            visibilityObserver.observe(iframe);
          },
          onStateChange: event => {
            switch (event.data) {
              case YT.PlayerState.PLAYING:
                console.log('YT.PlayerState.PLAYING');
                hasStartedPlaybackRef.current = true;
                setIsTouchInteracting(true);
                break;
              case YT.PlayerState.PAUSED:
                console.log('YT.PlayerState.PAUSED');
                break;
              case YT.PlayerState.ENDED:
                console.log('YT.PlayerState.ENDED');
                if (!hasStartedPlaybackRef.current || !hasVideoActuallyEnded(event.target)) {
                  break;
                }
                hasStartedPlaybackRef.current = false;
                dispatchVideoEndedEvent();
                break;
              case YT.PlayerState.BUFFERING:
                console.log('YT.PlayerState.BUFFERING');
                setIsTouchInteracting(true);
                break;
              default:
                break;
            }
          },
        },
      });
    });

    return () => {
      isUnmounted = true;
      visibilityObserver?.disconnect();
      if (playerRef.current?.destroy) {
        playerRef.current.destroy();
      }
      playerRef.current = null;
    };
  }, [autoplay, dispatchVideoEndedEvent, iframeId, loadedIframeId]);

  const enableTouchInteraction = () => {
    setIsTouchInteracting(true);
  };

  const onTouchLayerPointerDown = e => {
    touchStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      isSwipe: false,
    };
  };

  const onTouchLayerPointerMove = e => {
    const diffX = Math.abs(e.clientX - touchStartRef.current.x);
    const diffY = Math.abs(e.clientY - touchStartRef.current.y);
    touchStartRef.current.isSwipe = diffX > TOUCH_MOVE_THRESHOLD || diffY > TOUCH_MOVE_THRESHOLD;
  };

  const onTouchLayerPointerUp = () => {
    if (!touchStartRef.current.isSwipe) {
      enableTouchInteraction();
    }
  };

  const onTouchLayerPointerCancel = () => {
    touchStartRef.current.isSwipe = true;
  };

  return (
    <AspectRatioWrapper className={classes} width={width} height={height}>
      <LazyIFrame
        id={iframeId}
        src={`https://www.youtube-nocookie.com/embed/${youtubeVideoId}?${queryParams}`}
        className={classNames(css.iframe, { [css.touchInteracting]: isTouchInteracting })}
        style={{ background: BLACK_BG }}
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        onLoad={() => setLoadedIframeId(iframeId)}
        title="Embedded youtube"
      />
      <button
        type="button"
        className={classNames(css.touchLayer, {
          [css.touchLayerDisabled]: isTouchInteracting,
        })}
        aria-label="Enable video controls"
        onPointerDown={onTouchLayerPointerDown}
        onPointerMove={onTouchLayerPointerMove}
        onPointerUp={onTouchLayerPointerUp}
        onPointerCancel={onTouchLayerPointerCancel}
      />
    </AspectRatioWrapper>
  );
};

YoutubeEmbed.displayName = 'YoutubeEmbed';
